<template>
  <Layout title="" description="">
    <div class="px-4 sm:px-0">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Job Emails</h1>
          <p class="mt-2 text-gray-600">Emails forwarded from the AMD inbox, and what the system did with each one.</p>
        </div>
        <button
          class="btn btn-outline-secondary d-flex align-items-center gap-2 mt-4 sm:mt-0"
          :disabled="isLoading"
          @click="fetchEmails"
        >
          <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': isLoading }" />
          Refresh
        </button>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs mb-4">
        <li v-for="t in tabs" :key="t.key" class="nav-item">
          <button
            class="nav-link d-flex align-items-center gap-2"
            :class="{ active: tab === t.key }"
            @click="switchTab(t.key)"
          >
            {{ t.label }}
            <span class="badge" :class="t.key === 'review' && counts.review ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'">
              {{ countLabel(t.key) }}
            </span>
          </button>
        </li>
      </ul>

      <!-- Filters -->
      <div class="flex flex-col md:flex-row gap-2 mb-4">
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600">From</label>
          <input v-model="fromDate" type="date" class="form-control" @change="fetchEmails" />
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600">To</label>
          <input v-model="toDate" type="date" class="form-control" @change="fetchEmails" />
        </div>
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            v-model="searchTerm"
            placeholder="Search subject, sender or email text, then press Enter"
            class="w-full pl-10 pr-4 py-2 h-10 border rounded text-sm"
            @keyup.enter="fetchEmails"
          />
        </div>
        <button class="btn btn-primary" :disabled="isLoading" @click="fetchEmails">Search</button>
      </div>

      <p class="text-sm text-gray-500 mb-4">{{ tabHint }}</p>

      <!-- List -->
      <div v-if="isLoading" class="text-center text-gray-500 py-8">Loading emails...</div>
      <div v-else-if="!emails.length" class="text-center text-gray-500 py-8">
        {{ tab === 'review' ? 'Nothing needs review for these dates.' : 'No processed emails for these dates.' }}
      </div>
      <div v-else class="grid gap-3">
        <div
          v-for="e in emails"
          :key="e.uid"
          class="card bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
        >
          <!-- Header row (click to expand) -->
          <div
            class="flex flex-col md:flex-row md:justify-between md:items-center gap-2 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
            @click="toggleExpand(e)"
          >
            <div class="min-w-0">
              <h2 class="text-base font-semibold text-gray-900 truncate">{{ e.subject || '(no subject)' }}</h2>
              <p class="text-sm text-gray-600 truncate">{{ e.from }} • {{ formatDT(e.date) }}</p>
              <div class="flex flex-wrap items-center gap-2 mt-1">
                <span class="badge" :class="e.isReply ? 'bg-purple-100 text-purple-800' : 'bg-sky-100 text-sky-800'">
                  {{ e.isReply ? 'Reply' : 'New' }}
                </span>
                <span class="badge" :class="reasonBadge(e).color">{{ reasonBadge(e).label }}</span>
                <span v-for="j in e.jobs" :key="j.id" class="badge bg-indigo-100 text-indigo-800">
                  {{ jobLabel(j) }} · {{ j.status }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-3 flex-shrink-0">
              <button
                v-if="tab === 'review'"
                class="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium"
                :disabled="!!busy[e.uid]"
                @click.stop="retryEmail(e)"
              >
                <RotateCcw class="w-4 h-4" :class="{ 'animate-spin': busy[e.uid] === 'retry' }" />
                Retry
              </button>
              <button
                class="inline-flex items-center gap-1.5 text-sm font-medium"
                :class="tab === 'review' ? 'text-green-600 hover:text-green-800' : 'text-amber-600 hover:text-amber-800'"
                :disabled="!!busy[e.uid]"
                @click.stop="moveEmail(e)"
              >
                <ArrowRightLeft class="w-4 h-4" />
                {{ tab === 'review' ? 'Move to Done' : 'Move to Needs review' }}
              </button>
              <svg
                :class="['w-5 h-5 transition-transform', expanded[e.uid] ? 'rotate-180' : 'rotate-0']"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <!-- Details -->
          <transition name="fade">
            <div v-if="expanded[e.uid]" class="px-4 py-4 border-t border-gray-200 bg-white">
              <div v-if="!details[e.uid]" class="text-sm text-gray-500">Loading email...</div>
              <template v-else>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">From</h4>
                    <p class="text-sm text-gray-600 break-all">{{ details[e.uid].from || '—' }}</p>
                  </div>
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">To</h4>
                    <p class="text-sm text-gray-600 break-all">{{ details[e.uid].to || '—' }}</p>
                  </div>
                </div>

                <div v-if="details[e.uid].jobs.length" class="mb-4">
                  <h4 class="font-medium text-gray-900 mb-2">Jobs from this email</h4>
                  <div class="space-y-2">
                    <div
                      v-for="j in details[e.uid].jobs"
                      :key="j.id"
                      class="flex flex-wrap justify-between items-center gap-2 p-3 bg-gray-50 rounded-lg border"
                    >
                      <span class="text-gray-800 font-medium">{{ jobLabel(j) }} — {{ j.vesselName || 'No Vessel' }}</span>
                      <span class="text-sm text-gray-600">{{ formatDT(j.startTime) }} • {{ j.status }}</span>
                    </div>
                  </div>
                </div>

                <!-- Conversation -->
                <div class="mb-4">
                  <h4 class="font-medium text-gray-900 mb-2">Conversation</h4>
                  <p v-if="details[e.uid].thread.length <= 1" class="text-sm text-gray-600">
                    Single email: no other emails in this conversation.
                  </p>
                  <template v-else>
                    <p v-if="details[e.uid].threadMatchedBy === 'subject'" class="text-xs text-gray-500 mb-2">
                      Grouped by subject because these emails have no reply headers. Unrelated emails with the same subject can show up here.
                    </p>
                    <div class="space-y-1">
                      <div
                        v-for="t in details[e.uid].thread"
                        :key="t.tab + t.uid"
                        class="flex flex-wrap items-center gap-2 px-3 py-2 rounded border text-sm"
                        :class="isSameEmail(t, e) ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50'"
                      >
                        <span class="text-gray-500 thread-date">{{ formatDT(t.date) }}</span>
                        <span class="flex-1 min-w-0 truncate text-gray-800">
                          {{ t.subject || '(no subject)' }}
                          <span v-if="isSameEmail(t, e)" class="text-indigo-700 font-medium">(this email)</span>
                        </span>
                        <span class="badge bg-gray-100 text-gray-700">{{ t.tab === 'review' ? 'Needs review' : 'Done' }}</span>
                        <span v-if="!t.jobs.length" class="badge" :class="reasonBadge(t).color">{{ reasonBadge(t).label }}</span>
                        <span v-for="j in t.jobs" :key="j.id" class="badge bg-indigo-100 text-indigo-800">{{ jobLabel(j) }}</span>
                      </div>
                    </div>
                  </template>
                </div>

                <h4 class="font-medium text-gray-900 mb-2">Email</h4>
                <pre class="email-body">{{ details[e.uid].body || '(empty email)' }}</pre>
              </template>
            </div>
          </transition>
        </div>

        <p v-if="total > emails.length" class="text-sm text-gray-500 text-center">
          Showing the newest {{ emails.length }} of {{ total }}. Narrow the dates or search to see older emails.
        </p>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { Search, RefreshCw, RotateCcw, ArrowRightLeft } from 'lucide-vue-next'
import axios from 'axios'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import router from '@/router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useToast } from 'vue-toastification'

dayjs.extend(utc)
dayjs.extend(timezone)

const REASONS = {
  review: {
    error: { label: 'Failed', color: 'bg-red-100 text-red-800' },
    noJob: { label: 'No job found', color: 'bg-amber-100 text-amber-800' },
    manual: { label: 'Moved here by admin', color: 'bg-gray-100 text-gray-700' },
  },
  done: {
    processed: { label: 'Processed', color: 'bg-green-100 text-green-800' },
    dryRun: { label: 'Dry run (nothing saved)', color: 'bg-blue-100 text-blue-800' },
    manual: { label: 'Marked done by admin', color: 'bg-gray-100 text-gray-700' },
  },
}

export default {
  name: 'EmailInbox',
  components: { Layout, Search, RefreshCw, RotateCcw, ArrowRightLeft },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    const today = dayjs().tz('Asia/Singapore')
    return {
      tabs: [
        { key: 'review', label: 'Needs review' },
        { key: 'done', label: 'Done' },
      ],
      tab: 'review',
      fromDate: today.subtract(7, 'day').format('YYYY-MM-DD'),
      toDate: today.format('YYYY-MM-DD'),
      searchTerm: '',
      emails: [],
      total: 0,
      counts: {},
      expanded: {},
      details: {},
      busy: {},
      isLoading: false,
    }
  },
  computed: {
    tabHint() {
      return this.tab === 'review'
        ? 'No job was found in these emails, or reading them failed. Retry an email, or move it to Done if it is not a job (or you created the job by hand).'
        : 'These emails created or updated jobs. Move one back to Needs review if the result was wrong.'
    },
  },
  methods: {
    // ===== AUTH =====
    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')
      const token = await user.getIdToken()
      return { Authorization: token }
    },
    async checkAuthState() {
      const auth = getAuth()
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async user => {
          if (user) {
            this.token = await user.getIdToken()
            resolve()
          } else {
            router.push('/')
            reject(new Error('No user logged in'))
          }
        })
      })
    },
    checkPageAccess(allowedRoles = []) {
      const access = this.$store.state.systemAccess || {}
      const hasAccess = allowedRoles.some(role => access[role] === true)
      if (!hasAccess) {
        this.toast.error('Access denied.')
        if (access.client) {
          this.$router.push('/booking')
        } else if (access.driver) {
          this.$router.push('/jobManagement')
        } else {
          this.$router.push('/')
        }
        return false
      }
      return true
    },

    // ===== DATA =====
    async fetchEmails() {
      this.isLoading = true
      this.expanded = {}
      this.details = {}
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/api/job-emails`, {
          headers,
          params: { tab: this.tab, from: this.fromDate, to: this.toDate, q: this.searchTerm.trim() },
        })
        this.emails = res.data.emails || []
        this.total = res.data.total || 0
        this.counts = res.data.counts || {}
      } catch (err) {
        console.error('❌ fetchEmails error:', err)
        this.toast.error(this.errorText(err, 'Could not load emails.'))
        this.emails = []
      } finally {
        this.isLoading = false
      }
    },
    async toggleExpand(e) {
      this.expanded = { ...this.expanded, [e.uid]: !this.expanded[e.uid] }
      if (!this.expanded[e.uid] || this.details[e.uid]) return
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/api/job-emails/${this.tab}/${e.uid}`, { headers })
        this.details = { ...this.details, [e.uid]: res.data }
      } catch (err) {
        console.error('❌ fetch email error:', err)
        this.toast.error(this.errorText(err, 'Could not open this email.'))
        this.expanded = { ...this.expanded, [e.uid]: false }
      }
    },
    async moveEmail(e) {
      const to = this.tab === 'review' ? 'done' : 'review'
      this.busy = { ...this.busy, [e.uid]: 'move' }
      try {
        const headers = await this.getAuthHeader()
        await axios.post(`${this.$apiURL}/api/job-emails/${this.tab}/${e.uid}/move`, { to }, { headers })
        this.toast.success(to === 'done' ? 'Moved to Done' : 'Moved to Needs review')
        await this.fetchEmails()
      } catch (err) {
        console.error('❌ move email error:', err)
        this.toast.error(this.errorText(err, 'Could not move this email.'))
      } finally {
        this.busy = { ...this.busy, [e.uid]: null }
      }
    },
    async retryEmail(e) {
      this.busy = { ...this.busy, [e.uid]: 'retry' }
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.post(`${this.$apiURL}/api/job-emails/${this.tab}/${e.uid}/retry`, {}, { headers })
        const result = res.data.result || {}
        const dryRun = res.data.dryRun ? ' (dry run: nothing saved)' : ''
        if (result.error) {
          this.toast.error(`Still failing: ${result.error}`)
        } else if (res.data.tab === 'done') {
          this.toast.success(`Found ${result.jobs || 0} job(s), moved to Done${dryRun}`)
        } else {
          this.toast.info('Still no job found in this email')
        }
        await this.fetchEmails()
      } catch (err) {
        console.error('❌ retry email error:', err)
        this.toast.error(this.errorText(err, 'Retry failed.'))
      } finally {
        this.busy = { ...this.busy, [e.uid]: null }
      }
    },
    switchTab(key) {
      if (this.tab === key) return
      this.tab = key
      this.fetchEmails()
    },

    // ===== DISPLAY =====
    countLabel(key) {
      return this.counts[key] === undefined ? '–' : this.counts[key]
    },
    reasonBadge(e) {
      const tab = e.tab || this.tab
      if (tab === 'done' && e.reason === 'processed' && e.jobs.length) {
        return { label: `${e.jobs.length} job${e.jobs.length > 1 ? 's' : ''}`, color: 'bg-green-100 text-green-800' }
      }
      return REASONS[tab][e.reason] || { label: e.reason, color: 'bg-gray-100 text-gray-700' }
    },
    jobLabel(j) {
      return `${j.action === 'created' ? 'Created' : 'Updated'} ${j.id}`
    },
    isSameEmail(t, e) {
      return t.tab === this.tab && t.uid === e.uid
    },
    formatDT(iso) {
      if (!iso) return '—'
      return dayjs(iso).tz('Asia/Singapore').format('DD MMM YYYY, HH:mm')
    },
    errorText(err, fallback) {
      return (err.response && err.response.data && err.response.data.error) || fallback
    },
  },
  async mounted() {
    await this.checkAuthState()
    if (!this.checkPageAccess(['admin'])) return
    await this.fetchEmails()
  },
}
</script>

<style scoped>
.badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}
.email-body {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.875rem;
  color: #374151;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  max-height: 480px;
  overflow-y: auto;
}
.thread-date {
  min-width: 9rem;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
